import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Box, Heading, IconButton, Text } from '@wix/design-system';
import { ChevronLeftSmall, ChevronRightSmall } from '@wix/wix-ui-icons-common/lazy';
import s from './horizontal-nav-bar.module.scss';
import { horizontalNavBarDataHooks } from './data-hooks';
import { useHorizontalScroll } from './use-horizontal-scroll';

export interface HorizontalNavBarItem {
  key: string;
  label: string;
  href?: string;
}

interface HorizontalNavBarBaseProps {
  items: HorizontalNavBarItem[];
  selectedItemKey?: string;
  onItemClick(item: HorizontalNavBarItem, index: number): void;
}

interface PillsNavBarProps extends HorizontalNavBarBaseProps {
  skin?: 'pills';
  sideContent?: React.ReactNode;
}

interface TabsNavBarProps extends HorizontalNavBarBaseProps {
  skin: 'tabs';
  title: string;
}

export type HorizontalNavBarProps = PillsNavBarProps | TabsNavBarProps;

export function HorizontalNavBar(props: HorizontalNavBarProps) {
  if (props.skin === 'tabs') {
    return <TabsNavBar {...props} />;
  }
  return <PillsNavBar {...props} />;
}

function TabsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  title,
}: TabsNavBarProps) {
  return (
    <Box
      width="100%"
      gap="24px"
      verticalAlign="middle"
      dataHook={horizontalNavBarDataHooks.root}
    >
      <Heading size="extraLarge" dataHook={horizontalNavBarDataHooks.title}>
        {title}
      </Heading>
      <ul className={s.tabs}>
        {items.map((item, index) => {
          const isSelected = item.key === selectedItemKey;
          return (
            <li key={item.key}>
              <button
                type="button"
                data-hook={horizontalNavBarDataHooks.item(item.key)}
                className={classNames(s.tab, { [s.tabSelected]: isSelected })}
                aria-current={isSelected ? 'page' : undefined}
                onClick={() => onItemClick(item, index)}
              >
                <Text size="small" secondary={!isSelected}>
                  {item.label}
                </Text>
              </button>
            </li>
          );
        })}
      </ul>
    </Box>
  );
}

function PillsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  sideContent,
}: PillsNavBarProps) {
  const {
    scrollerRef: itemsRef,
    canScrollBack,
    canScrollForward,
    scrollByChunk,
  } = useHorizontalScroll<HTMLUListElement>(items);

  /* items overflow into horizontal scroll - keep the selected one visible */
  useEffect(() => {
    const selectedItem = itemsRef.current?.querySelector(
      '[aria-current="page"]',
    );
    selectedItem?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [itemsRef, selectedItemKey]);

  return (
    <nav
      className={s.horizontalNavBar}
      data-hook={horizontalNavBarDataHooks.root}
    >
      <Box className={s.itemsContainer}>
        <Box
          className={classNames(s.arrowOverlay, s.arrowOverlayBack, {
            [s.arrowVisible]: canScrollBack,
          })}
        >
          <IconButton
            size="tiny"
            skin="standard"
            priority="secondary"
            ariaLabel="Scroll back"
            dataHook={horizontalNavBarDataHooks.scrollArrow('back')}
            className={s.scrollArrow}
            aria-hidden={!canScrollBack}
            tabIndex={canScrollBack ? 0 : -1}
            data-visible={canScrollBack}
            onClick={() => scrollByChunk(-1)}
          >
            <ChevronLeftSmall />
          </IconButton>
        </Box>
        <ul className={s.items} ref={itemsRef}>
          {items.map((item, index) => {
            const isSelected = item.key === selectedItemKey;
            /* support href on SA, and prevent-default navigation in other containers */
            const PillTag = item.href ? 'a' : 'button';
            return (
              <li key={item.key} className={s.item}>
                <PillTag
                  data-hook={horizontalNavBarDataHooks.item(item.key)}
                  className={classNames(s.pill, { [s.selected]: isSelected })}
                  href={item.href}
                  type={item.href ? undefined : 'button'}
                  aria-current={isSelected ? 'page' : undefined}
                  onClick={(event: React.MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onItemClick(item, index);
                  }}
                >
                  <Text size="small" light={isSelected}>
                    {item.label}
                  </Text>
                </PillTag>
              </li>
            );
          })}
        </ul>
        <Box
          className={classNames(s.arrowOverlay, s.arrowOverlayForward, {
            [s.arrowVisible]: canScrollForward,
          })}
        >
          <IconButton
            size="tiny"
            skin="standard"
            priority="secondary"
            ariaLabel="Scroll forward"
            dataHook={horizontalNavBarDataHooks.scrollArrow('forward')}
            className={s.scrollArrow}
            aria-hidden={!canScrollForward}
            tabIndex={canScrollForward ? 0 : -1}
            data-visible={canScrollForward}
            onClick={() => scrollByChunk(1)}
          >
            <ChevronRightSmall />
          </IconButton>
        </Box>
      </Box>
      {sideContent && (
        <Box
          className={s.sideContent}
          dataHook={horizontalNavBarDataHooks.sideContent}
        >
          {sideContent}
        </Box>
      )}
    </nav>
  );
}
